import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { 
  getAllTimeDoctorUsers,
  getSingleTimeDoctorUser,
  getScreenshots,
  getActivity,
  getTimeUse,
  getProjects,
  getTasks,
  getClientAssignedBillers
} from '../API/timeDoctor';
import { getAllBiller } from '../API/biller';
import dayjs from 'dayjs';

const initialState = {
  billersList: [],
  loading: false,
  error: null,
};

const ActionTypes = {
  SET_BILLERS_LIST: 'SET_BILLERS_LIST',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
};

const timeDoctorReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_BILLERS_LIST:
      return { ...state, billersList: action.data, loading: false, error: null };
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.value };
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.value, loading: false };
    default:
      return state;
  }
};

const TimeDoctorContext = createContext();

export const TimeDoctorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(timeDoctorReducer, initialState);

  // Get billers list (CLIENT: assigned billers with TD mapping, ADMIN: all TD users)
  const getBillersList = useCallback(async (role, userId) => {
    dispatch({ type: ActionTypes.SET_LOADING, value: true });
    try {
      let billers = [];
      if (role === 'CLIENT') {
        billers = await getClientAssignedBillers();
      } else if (role === 'ADMIN') {
        billers = await getAllTimeDoctorUsers();
      } else if (role === 'BILLER') {
        // For biller role, just return self from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          billers = [{ userId: user.userId, userName: user.userName }];
        }
      }
      dispatch({ type: ActionTypes.SET_BILLERS_LIST, data: billers });
      return billers;
    } catch (error) {
      dispatch({ type: ActionTypes.SET_ERROR, value: error });
      return [];
    }
  }, []);

  // Get single user details (for report page)
  const getUserDetails = useCallback(async (userId) => {
    return await getSingleTimeDoctorUser(userId);
  }, []);

  // Get screenshots
  const getUserScreenshots = useCallback(async (userId, from, to) => {
    const res = await getScreenshots(userId, from, to);
    // If backend returns { data: [...] }, return the array
    if (res && Array.isArray(res.data)) return res.data;
    return res;
  }, []);

  // Get activity
  const getUserActivity = useCallback(async (userId, from, to) => {
    const res = await getActivity(userId, from, to);
    // If backend returns { data: [[...]] } or { data: [...] }, flatten if needed
    if (res && Array.isArray(res.data)) {
      if (Array.isArray(res.data[0])) {
        // Nested array: [[...]]
        return res.data.flat();
      }
      // Flat array: [...]
      return res.data;
    }
    return res;
  }, []);

  // Get productivity/time use
  const getUserTimeUse = useCallback(async (userId, from, to) => {
    const res = await getTimeUse(userId, from, to);
    // If backend returns { data: [[...]] } or { data: [...] }, flatten if needed
    if (res && Array.isArray(res.data)) {
      if (Array.isArray(res.data[0])) {
        // Nested array: [[...]]
        return res.data.flat();
      }
      // Flat array: [...]
      return res.data;
    }
    return res;
  }, []);

  // Get all projects
  const getAllProjects = useCallback(async () => {
    return await getProjects();
  }, []);

  // Get all tasks
  const getAllTasks = useCallback(async () => {
    return await getTasks();
  }, []);

  // Add getWorklogDataOptimized to the context
  const getWorklogDataOptimized = useCallback(async (role, userId, viewMode, selectedDate) => {
    // Support ADMIN and CLIENT roles
    if (role !== 'ADMIN' && role !== 'CLIENT') return [];
    
    // Determine date range
    let from, to;
    if (viewMode === 'monthly') {
      from = dayjs(selectedDate).startOf('month').format('YYYY-MM-DD');
      to = dayjs(selectedDate).endOf('month').format('YYYY-MM-DD');
    } else if (viewMode === 'weekly') {
      from = dayjs(selectedDate).startOf('week').format('YYYY-MM-DD');
      to = dayjs(selectedDate).endOf('week').format('YYYY-MM-DD');
    } else {
      from = dayjs(selectedDate).format('YYYY-MM-DD');
      to = from;
    }
    
    // Fetch billers based on role
    let billers = [];
    if (role === 'ADMIN') {
      billers = await getAllTimeDoctorUsers();
    } else if (role === 'CLIENT') {
      billers = await getClientAssignedBillers();
    }
    
    // Build a mapping from Time Doctor user ID to DB user ID and biller name
    const tdIdToDbId = {};
    const tdIdToName = {};
    billers.forEach(biller => {
      // Handle both data structures: getAllTimeDoctorUsers vs getClientAssignedBillers
      const tdUserId = biller.id || biller.timeDoctorUserId;
      const dbUserId = biller.userId;
      const billerName = biller.name || biller.userName;
      
      if (tdUserId && dbUserId) {
        tdIdToDbId[tdUserId] = dbUserId;
        tdIdToName[tdUserId] = billerName;
      }
    });
    
    // For each biller, fetch their activity for the date range
    const allEntries = [];
    for (const biller of billers) {
      if (!biller.userId) {
        console.warn('Skipping biller with missing userId:', biller);
        continue;
      }
      // getUserActivity returns an array of activities for the date range
      let activities = await getActivity(biller.userId, from, to);
      if (activities && activities.data) activities = activities.data;
      if (!Array.isArray(activities)) activities = [];
      // Add dbUserId and billerName to each activity
      activities.forEach(act => {
        act.dbUserId = tdIdToDbId[act.userId] || null;
        act.billerName = tdIdToName[act.userId] || biller.name || biller.userName;
      });
      // Only keep activities with a valid mapping
      activities = activities.filter(act => {
        act.dbUserId = tdIdToDbId[act.userId] || null;
        act.billerName = tdIdToName[act.userId] || biller.name || biller.userName;
        return !!act.dbUserId;
      });
      // Group activities by date
      const grouped = {};
      activities.forEach(act => {
        const dateStr = dayjs(act.start).format('YYYY-MM-DD');
        if (!grouped[dateStr]) grouped[dateStr] = [];
        grouped[dateStr].push(act);
      });
      Object.keys(grouped).forEach(dateStr => {
        const acts = grouped[dateStr];
        const hoursWorked = acts.reduce((sum, a) => sum + (a.time || 0), 0) / 3600;
        const projectIds = new Set(acts.map(a => a.projectId).filter(Boolean));
        allEntries.push({
          billerName: acts[0].billerName,
          date: dayjs(dateStr),
          hoursWorked,
          projectCount: projectIds.size
        });
      });
    }
    // Sort by date descending (latest first), then by billerName
    allEntries.sort((a, b) => {
      if (a.date.isAfter(b.date)) return -1;
      if (a.date.isBefore(b.date)) return 1;
      return a.billerName.localeCompare(b.billerName);
    });
    return allEntries;
  }, []);

  // Add optimized billers list function for client role
  const getBillersListOptimized = useCallback(async (role, userId) => {
    try {
      let billers = [];
      if (role === 'CLIENT') {
        billers = await getClientAssignedBillers();
      } else if (role === 'ADMIN') {
        billers = await getAllTimeDoctorUsers();
      } else if (role === 'BILLER') {
        // For biller role, just return self from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          billers = [{ userId: user.userId, userName: user.userName }];
        }
      }
      return billers;
    } catch (error) {
      console.error('Error in getBillersListOptimized:', error);
      return [];
    }
  }, []);

  // Add dashboard stats function for client role
  const getDashboardStatsOptimized = useCallback(async (role, userId) => {
    try {
      if (role !== 'CLIENT') {
        return {
          activeBillers: 0,
          todayHours: '0.0',
          weeklyHours: '0.0',
          monthlyHours: '0.0'
        };
      }

      // Get client's assigned billers
      const billers = await getClientAssignedBillers();
      
      if (billers.length === 0) {
        return {
          activeBillers: 0,
          todayHours: '0.0',
          weeklyHours: '0.0',
          monthlyHours: '0.0'
        };
      }

      // Calculate stats for the current month
      const now = dayjs();
      const from = now.startOf('month').format('YYYY-MM-DD');
      const to = now.endOf('month').format('YYYY-MM-DD');
      
      let totalHours = 0;
      let activeBillers = 0;

      // For each biller, get their activity for the current month
      for (const biller of billers) {
        if (!biller.timeDoctorUserId) continue;
        
        try {
          let activities = await getActivity(biller.userId, from, to);
          if (activities && activities.data) activities = activities.data;
          if (!Array.isArray(activities)) activities = [];
          
          // Calculate hours for this biller
          const billerHours = activities.reduce((sum, act) => sum + (act.time || 0), 0) / 3600;
          totalHours += billerHours;
          
          if (billerHours > 0) {
            activeBillers++;
          }
        } catch (error) {
          console.error(`Error getting activity for biller ${biller.userId}:`, error);
        }
      }

      return {
        activeBillers,
        todayHours: '0.0', // TODO: Calculate today's hours
        weeklyHours: '0.0', // TODO: Calculate weekly hours
        monthlyHours: totalHours.toFixed(1)
      };
    } catch (error) {
      console.error('Error in getDashboardStatsOptimized:', error);
      return {
        activeBillers: 0,
        todayHours: '0.0',
        weeklyHours: '0.0',
        monthlyHours: '0.0'
      };
    }
  }, []);

  const value = {
    ...state,
    getBillersList,
    getUserDetails,
    getUserScreenshots,
    getUserActivity,
    getUserTimeUse,
    getAllProjects,
    getAllTasks,
    getWorklogDataOptimized,
    getBillersListOptimized,
    getDashboardStatsOptimized,
  };

  return (
    <TimeDoctorContext.Provider value={value}>
      {children}
    </TimeDoctorContext.Provider>
  );
};

export const useTimeDoctor = () => {
  const context = useContext(TimeDoctorContext);
  if (!context) {
    throw new Error('useTimeDoctor must be used within a TimeDoctorProvider');
  }
  return context;
};

export default TimeDoctorContext; 